/* eslint-disable no-console */
/* eslint-disable no-nested-ternary */
import { BadgeInfo, CircleX, Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import { Button } from '../../components/ui/button';
import { DialogClose, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import Label from '../../components/ui/label';
import Input from '../../components/ui/input';
import useAuth from '../../stores/useAuth';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../components/ui/form';
import useAddress from '../../stores/useAddress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ICompany } from '../../types/types';
import { FileUploadSimple } from '../../components/InputImage';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../components/ui/input-otp';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../components/ui/hover-card';

export function FormPassword() {
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { loading, changePass, dataUser } = useAuth();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const FormSchema = z
    .object({
      oldPassword: z.string(),
      newPassword: z.string(),
      confirmPassword: z.string(),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: "Passwords don't match",
      path: ['confirmPassword'],
    });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const closeRef = useRef<HTMLButtonElement>(null);
  const handleCloseDialog = () => {
    closeRef.current?.click();
  };

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    if (dataUser) {
      const { success } = await changePass(dataUser?.user_id as string, data);
      if (success) {
        handleCloseDialog();
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogHeader>
          <DialogTitle className="text-left">Change Email</DialogTitle>
          <DialogDescription className="text-left">
            <div className="py-4">
              <p>
                Please enter your current password and a new password to update your account. Make sure your new
                password is strong and secure.
              </p>
              <div className="flex flex-col gap-4 mt-4">
                <FormField
                  name="oldPassword"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="oldPassword" className="text-primary">
                        Current Password
                      </FormLabel>
                      <div className="relative">
                        <Input
                          id="oldPassword"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="******"
                          {...field}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={togglePasswordVisibility}
                          className="absolute inset-y-0 right-0 flex items-center px-3"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="newPassword"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="newPassword" className="text-primary">
                        New Password
                      </FormLabel>
                      <div className="relative">
                        <Input
                          id="newPassword"
                          type={showNewPassword ? 'text' : 'password'}
                          placeholder="******"
                          {...field}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={toggleNewPasswordVisibility}
                          className="absolute inset-y-0 right-0 flex items-center px-3"
                        >
                          {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="confirmPassword"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="confirmPassword" className="text-primary">
                        Confirm Password
                      </FormLabel>
                      <div className="relative">
                        <Input
                          id="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="******"
                          {...field}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={toggleConfirmPasswordVisibility}
                          className="absolute inset-y-0 right-0 flex items-center px-3"
                        >
                          {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-row items-center justify-end gap-2">
          <Button type="submit" variant="default" className="w-max" disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
          <DialogClose ref={closeRef}>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </form>
    </Form>
  );
}

export function FormName() {
  const { updateNameUser, dataUser, loading } = useAuth();
  const FormSchema = z.object({
    name: z.string(),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: dataUser?.name,
    },
  });

  const closeRef = useRef<HTMLButtonElement>(null);
  const handleCloseDialog = () => {
    closeRef.current?.click();
  };

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    if (dataUser) {
      const { success } = await updateNameUser(dataUser?.user_id as string, data);
      if (success) {
        handleCloseDialog();
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogHeader>
          <DialogTitle className="text-left">Change Name</DialogTitle>
          <DialogDescription className="text-left">
            <div className="py-4">
              <p>
                Please enter your new name to update your account. Make sure to use your full name as it will be
                displayed on your profile and used for identification purposes.
              </p>
              <div className="flex flex-col gap-4 mt-4">
                <FormField
                  name="name"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="name" className="font-semibold text-text">
                        Name
                      </FormLabel>
                      <Input id="name" className="col-span-3" {...field} />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-row items-center justify-end gap-2">
          <Button type="submit" variant="default" className="w-max" disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
          <DialogClose ref={closeRef}>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </form>
    </Form>
  );
}

export function FormChangeEmail() {
  const { dataUser, changeEmail, loading } = useAuth();

  // Schema for the initial email change request
  const EmailSchema = z.object({
    newEmail: z
      .string()
      .email('Invalid email address')
      .refine((email) => email !== dataUser?.email, {
        message: 'New email must be different from current email',
      }),
  });

  // Form for email change request
  const emailForm = useForm<z.infer<typeof EmailSchema>>({
    resolver: zodResolver(EmailSchema),
    defaultValues: {
      newEmail: '',
    },
  });

  const closeRef = useRef<HTMLButtonElement>(null);
  const handleCloseDialog = () => {
    closeRef.current?.click();
  };

  // Handle initial email change request
  async function onEmailSubmit(data: z.infer<typeof EmailSchema>) {
    const { success } = await await changeEmail(dataUser?.user_id as string, data);
    if (success) {
      handleCloseDialog();
    }
  }

  return (
    <Form {...emailForm}>
      <form onSubmit={emailForm.handleSubmit(onEmailSubmit)}>
        <DialogHeader>
          <DialogTitle className="text-left">Change Email</DialogTitle>
          <DialogDescription className="text-left">
            <div className="py-4">
              <p>Enter your new email address.</p>
              <div className="flex flex-col gap-4 mt-4">
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-500">Current email: {dataUser?.email}</p>
                </div>
                <FormField
                  name="newEmail"
                  control={emailForm.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="email" className="font-semibold text-text">
                        New Email
                      </FormLabel>
                      <Input id="email" className="col-span-3" placeholder="Enter your new email" {...field} />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-row items-center justify-end gap-2">
          <Button type="submit" variant="default" className="w-max" disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
          <DialogClose ref={closeRef}>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </form>
    </Form>
  );
}

// export function FormChangeEmail() {
//   const { dataUser, sendEmailVerify, verifyOtp, changeEmail, loading } = useAuth();
//   const [step, setStep] = useState<'initial' | 'verification' | 'success'>('initial');
//   const [newEmail, setNewEmail] = useState('');

//   // Schema for the initial email change request
//   const EmailSchema = z.object({
//     newEmail: z
//       .string()
//       .email('Invalid email address')
//       .refine((email) => email !== dataUser?.email, {
//         message: 'New email must be different from current email',
//       }),
//   });

//   // Schema for OTP verification
//   const OTPSchema = z.object({
//     otp: z.string().length(6, 'OTP must be 6 digits'),
//   });

//   // Form for email change request
//   const emailForm = useForm<z.infer<typeof EmailSchema>>({
//     resolver: zodResolver(EmailSchema),
//     defaultValues: {
//       newEmail: '',
//     },
//   });

//   // Form for OTP verification
//   const otpForm = useForm<z.infer<typeof OTPSchema>>({
//     resolver: zodResolver(OTPSchema),
//     defaultValues: {
//       otp: '',
//     },
//   });

//   // Handle initial email change request
//   async function onEmailSubmit(data: z.infer<typeof EmailSchema>) {
//     // Send request to initiate email change
//     await sendEmailVerify(dataUser?.user_id as string, data);

//     // Store new email for later use
//     setNewEmail(data?.newEmail);

//     // Move to OTP verification step
//     setStep('verification');
//   }

//   // Handle OTP verification
//   async function onOTPSubmit(data: z.infer<typeof OTPSchema>) {
//     // Verify OTP
//     const { success } = await verifyOtp(dataUser?.user_id as string, data);
//     if (success) {
//       // Complete email change process
//       const response = await changeEmail(dataUser?.user_id as string, { email: newEmail });
//       if (response.success) {
//         setStep('success');
//       }
//     }
//   }

//   return (
//     <div>
//       {step === 'initial' && (
//         <Form {...emailForm}>
//           <form onSubmit={emailForm.handleSubmit(onEmailSubmit)}>
//             <DialogHeader>
//               <DialogTitle className="text-left">Change Email</DialogTitle>
//               <DialogDescription className="text-left">
//                 <div className="py-4">
//                   <p>Enter your new email address. A verification code will be sent to your current email.</p>
//                   <div className="flex flex-col gap-4 mt-4">
//                     <div className="mb-4">
//                       <p className="text-sm font-medium text-gray-500">Current email: {dataUser?.email}</p>
//                     </div>
//                     <FormField
//                       name="newEmail"
//                       control={emailForm.control}
//                       render={({ field }) => (
//                         <FormItem>
//                           <FormLabel htmlFor="email" className="font-semibold text-text">
//                             New Email
//                           </FormLabel>
//                           <Input id="email" className="col-span-3" placeholder="Enter your new email" {...field} />
//                           <FormMessage />
//                         </FormItem>
//                       )}
//                     />
//                   </div>
//                 </div>
//               </DialogDescription>
//             </DialogHeader>
//             <DialogFooter className="flex flex-row items-center justify-end gap-2">
//               <Button type="submit" variant="default" className="w-max" disabled={loading}>
//                 {loading ? 'Sending...' : 'Continue'}
//               </Button>
//               <DialogClose>
//                 <Button type="button" variant="outline">
//                   Cancel
//                 </Button>
//               </DialogClose>
//             </DialogFooter>
//           </form>
//         </Form>
//       )}

//       {step === 'verification' && (
//         <Form {...otpForm}>
//           <form onSubmit={otpForm.handleSubmit(onOTPSubmit)}>
//             <DialogHeader>
//               <DialogTitle className="text-left">Verify Email Change</DialogTitle>
//               <DialogDescription className="text-left">
//                 <div className="py-4">
//                   <p>
//                     A verification code has been sent to your current email address ({dataUser?.email}). Enter the
//                     6-digit code below to confirm your email change.
//                   </p>
//                   <div className="flex flex-col gap-4 mt-4">
//                     <p className="text-sm font-medium">
//                       New email: <span className="font-semibold">{newEmail}</span>
//                     </p>
//                     <FormField
//                       control={otpForm.control}
//                       name="otp"
//                       render={({ field }) => (
//                         <FormItem>
//                           <FormLabel htmlFor="otp" className="font-semibold text-text">
//                             Verification Code
//                           </FormLabel>
//                           <FormControl>
//                             <InputOTP maxLength={6} {...field}>
//                               <InputOTPGroup>
//                                 <InputOTPSlot index={0} />
//                                 <InputOTPSlot index={1} />
//                                 <InputOTPSlot index={2} />
//                                 <InputOTPSlot index={3} />
//                                 <InputOTPSlot index={4} />
//                                 <InputOTPSlot index={5} />
//                               </InputOTPGroup>
//                             </InputOTP>
//                           </FormControl>
//                           <FormDescription>The verification code is valid for 10 minutes.</FormDescription>
//                           <FormMessage />
//                         </FormItem>
//                       )}
//                     />
//                   </div>
//                 </div>
//               </DialogDescription>
//             </DialogHeader>
//             <DialogFooter className="flex flex-row items-center justify-end gap-2">
//               <Button type="submit" variant="default" className="w-max" disabled={loading}>
//                 {loading ? 'Verifying...' : 'Verify and Change Email'}
//               </Button>
//               <Button type="button" variant="outline" onClick={() => setStep('initial')}>
//                 Back
//               </Button>
//             </DialogFooter>
//           </form>
//         </Form>
//       )}

//       {step === 'success' && (
//         <div>
//           <DialogHeader>
//             <DialogTitle className="text-left">Email Changed Successfully</DialogTitle>
//             <DialogDescription className="text-left">
//               <div className="py-4">
//                 <p>
//                   Your email has been successfully changed to <span className="font-semibold">{newEmail}</span>.
//                 </p>
//               </div>
//             </DialogDescription>
//           </DialogHeader>
//           <DialogFooter className="flex flex-row items-center justify-end gap-2">
//             <DialogClose>
//               <Button type="button" variant="default">
//                 Done
//               </Button>
//             </DialogClose>
//           </DialogFooter>
//         </div>
//       )}
//     </div>
//   );
// }

export function FormCompanyEmail() {
  const { company, updateCompany, loading } = useAuth();
  const FormSchema = z.object({
    email: z.string(),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      email: company?.email,
    },
  });

  const closeRef = useRef<HTMLButtonElement>(null);
  const handleCloseDialog = () => {
    closeRef.current?.click();
  };

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    if (company?.company_id) {
      await updateCompany(company.company_id, data as ICompany);
      handleCloseDialog();
    } else {
      toast.warning("'Company ID is undefined'", {
        position: 'top-right',
      });
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogHeader>
          <DialogTitle className="text-left">Change Email</DialogTitle>
          <DialogDescription className="text-left">
            <div className="py-4">
              <p>
                Updated information will only be applied to future documents. Make sure to fill in the correct data.
              </p>
              <div className="flex flex-col gap-4 mt-4">
                <FormField
                  name="email" // Nama field
                  control={form.control} // Kontrol dari useForm
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="email" className="font-semibold text-text">
                        Email
                      </FormLabel>
                      <Input id="email" className="col-span-3" {...field} />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-row items-center justify-end gap-2">
          <Button type="submit" variant="default" className="w-max" disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
          <DialogClose ref={closeRef}>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </form>
    </Form>
  );
}

export function FormCompanyPhone() {
  const { company, updateCompany, loading } = useAuth();
  const FormSchema = z.object({
    phone: z.string(),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      phone: company?.phone,
    },
  });

  const closeRef = useRef<HTMLButtonElement>(null);
  const handleCloseDialog = () => {
    closeRef.current?.click();
  };

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    if (company?.company_id) {
      await updateCompany(company.company_id, data as ICompany);
      handleCloseDialog();
    } else {
      toast.warning("'Company ID is undefined'", {
        position: 'top-right',
      });
    }
  }
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogHeader>
          <DialogTitle className="text-left">Change Phone Number</DialogTitle>
          <DialogDescription className="text-left">
            <div className="py-4">
              <p>
                Updated information will only be applied to future documents. Make sure to fill in the correct data.
              </p>
              <div className="flex flex-col gap-4 mt-4">
                <FormField
                  name="phone" // Nama field
                  control={form.control} // Kontrol dari useForm
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="phone" className="font-semibold text-text">
                        Phone Number
                      </FormLabel>
                      <Input id="phone" className="col-span-3" {...field} />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-row items-center justify-end gap-2">
          <Button type="submit" variant="default" className="w-max" disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
          <DialogClose ref={closeRef}>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </form>
    </Form>
  );
}

export function FormCompanyInformation() {
  const { company, updateCompany, loading } = useAuth();
  const FormSchema = z.object({
    name: z.string().nullable(),
    taxIdent: z.string().nullable(),
    website: z.string().nullable(),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: company?.name || '',
      taxIdent: company?.taxIdent || '',
      website: company?.website || '',
    },
  });

  const closeRef = useRef<HTMLButtonElement>(null);
  const handleCloseDialog = () => {
    closeRef.current?.click();
  };

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    if (company?.company_id) {
      await updateCompany(company.company_id, data as ICompany);
      handleCloseDialog();
    } else {
      toast.warning("'Company ID is undefined'", {
        position: 'top-right',
      });
    }
  }
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogHeader>
          <DialogTitle className="text-left">Company Information</DialogTitle>
          <DialogDescription className="text-left">
            <div className="py-4">
              <p>
                Updated information will only be applied to future documents. Make sure to fill in the correct data.
              </p>
              <div className="flex flex-col gap-4 mt-4">
                <FormField
                  name="name" // Nama field
                  control={form.control} // Kontrol dari useForm
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold text-text">Company Name</FormLabel>
                      <Input className="col-span-3" {...field} value={field.value ?? ''} />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="taxIdent" // Nama field
                  control={form.control} // Kontrol dari useForm
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold text-text">Tax Identification</FormLabel>
                      <Input className="col-span-3" {...field} value={field.value ?? ''} />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="website" // Nama field
                  control={form.control} // Kontrol dari useForm
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold text-text">Website</FormLabel>
                      <Input className="col-span-3" {...field} value={field.value ?? ''} />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-row items-center justify-end gap-2">
          <Button type="submit" variant="default" className="w-max" disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
          <DialogClose ref={closeRef}>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </form>
    </Form>
  );
}

export function FormSign() {
  const [changeLogo, setChangeLogo] = useState(false);
  const [changeSign, setChangeSign] = useState(false);
  const { company, updateCompany, loading } = useAuth();
  const FormSchema = z.object({
    logo: z.string().nullable(),
    signature: z.string().nullable(),
    signature_name: z.string().nullable(),
    notes: z.string().nullable(),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      logo: company?.logo || '',
      signature: company?.signature || '',
      signature_name: company?.signature_name || '',
      notes: company?.notes || '',
    },
  });

  const closeRef = useRef<HTMLButtonElement>(null);
  const handleCloseDialog = () => {
    closeRef.current?.click();
  };

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    console.log('form data', data);

    if (company?.company_id) {
      const response = await updateCompany(company.company_id, data as ICompany);
      if (response.success) {
        handleCloseDialog();
      }
    } else {
      toast.warning("'Company ID is undefined'", {
        position: 'top-right',
      });
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogHeader>
          <DialogTitle className="text-left">Logo and Document Signature</DialogTitle>
          <DialogDescription className="text-left">
            <div className="py-4">
              <p>
                Updated information will only be applied to future documents. Make sure to fill in the correct data.
              </p>
              <div className="grid grid-cols-2 gap-2 mt-4">
                <Label className="relative flex flex-col gap-2 text-text font-semibold">
                  <h3>Logo</h3>
                  {company?.logo && !changeLogo ? (
                    <>
                      <img src={company?.logo} alt="Logo" className="w-36 h-36 md:w-60 md:h-60 rounded-md" />
                      <FormField
                        name="logo" // Nama field
                        control={form.control} // Kontrol dari useForm
                        render={({ field }) => (
                          <FormItem className="hidden">
                            <Input {...field} value={company?.logo} />
                          </FormItem>
                        )}
                      />
                      <button
                        className="absolute top-6 right-6 text-white/35 hover:text-red-600/70 bg-primary/30 hover:bg-transparent transition-all p-1 rounded-full"
                        onClick={() => setChangeLogo(true)}
                        type="button"
                      >
                        <CircleX />
                      </button>
                    </>
                  ) : (
                    <FileUploadSimple setValue={form.setValue} imageFieldName="logo" form={form} />
                  )}
                </Label>
                <Label className="relative flex flex-col gap-2 text-text font-semibold">
                  <h3>Signature</h3>
                  {company?.signature && !changeSign ? (
                    <>
                      <img src={company?.signature} alt="Signature" className="w-36 h-36 md:w-60 md:h-60 rounded-md" />
                      <FormField
                        name="signature" // Nama field
                        control={form.control} // Kontrol dari useForm
                        render={({ field }) => (
                          <FormItem className="hidden">
                            <Input {...field} value={company?.signature} />
                          </FormItem>
                        )}
                      />
                      <button
                        className="absolute top-6 right-6 text-white/35 hover:text-red-600/70 bg-primary/30 hover:bg-transparent transition-all p-1 rounded-full"
                        onClick={() => setChangeSign(true)}
                        type="button"
                      >
                        <CircleX />
                      </button>
                    </>
                  ) : (
                    <FileUploadSimple setValue={form.setValue} imageFieldName="signature" form={form} />
                  )}
                </Label>
              </div>
              <FormField
                name="signature_name" // Nama field
                control={form.control} // Kontrol dari useForm
                render={({ field }) => (
                  <FormItem className="mt-4">
                    <FormLabel className="font-semibold text-text">Signature Name</FormLabel>
                    <Input className="row-span-3 text-text" {...field} value={field.value ?? ''} />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="notes" // Nama field
                control={form.control} // Kontrol dari useForm
                render={({ field }) => (
                  <FormItem className="mt-4">
                    <div className="flex gap-1 items-center">
                      <FormLabel className="font-semibold text-text">Notes</FormLabel>
                      <HoverCard>
                        <HoverCardTrigger>
                          <BadgeInfo className="w-4 h-4 md:w-6 md:h-6" />
                        </HoverCardTrigger>
                        <HoverCardContent>
                          You can add notes for each invoice document created in the Settings
                        </HoverCardContent>
                      </HoverCard>
                    </div>
                    <ReactQuill
                      theme="bubble"
                      className={`${
                        form.formState.errors.notes ? 'border-red-500' : ''
                      } w-full min-h-32 rounded-md border border-neutral-200`}
                      {...field}
                      value={field.value ?? ''}
                      onChange={(content) => field.onChange(content)}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-row items-center justify-end gap-2">
          <Button type="submit" variant="default" className="w-max" disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
          <DialogClose ref={closeRef}>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </form>
    </Form>
  );
}

export function FormCompanyAddress() {
  const {
    loading,
    provinsi,
    district,
    city,
    selectedCity,
    selectedProvinsi,
    setSelectedCity,
    setSelectedDistrict,
    setSelectedProvinsi,
    fetchAllProvinsi,
    fetchCity,
    fetchDistrict,
  } = useAddress();
  const { company, updateCompany } = useAuth();
  const FormSchema = z.object({
    country: z.string().nullable(),
    state: z.string().nullable(),
    city: z.string().nullable(),
    district: z.string().nullable(),
    address_line_1: z.string().nullable(),
    address_line_2: z.string().nullable(),
    zip_code: z.string().nullable(),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      country: 'indonesia',
      state: company?.state || '',
      city: company?.city || '',
      district: company?.district || '',
      address_line_1: company?.address_line_1 || '',
      address_line_2: company?.address_line_2 || '',
      zip_code: company?.zip_code || '',
    },
  });

  const closeRef = useRef<HTMLButtonElement>(null);
  const handleCloseDialog = () => {
    closeRef.current?.click();
  };

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    if (company?.company_id) {
      await updateCompany(company.company_id, data as ICompany);
      handleCloseDialog();
    } else {
      toast.warning("'Company ID is undefined'", {
        position: 'top-right',
      });
    }
  }

  useEffect(() => {
    if (provinsi?.length === 0) {
      fetchAllProvinsi();
    }
  }, [provinsi?.length, fetchAllProvinsi]);

  useEffect(() => {
    if (selectedProvinsi) {
      fetchCity(selectedProvinsi);
    }
  }, [selectedProvinsi, fetchCity]);

  useEffect(() => {
    if (selectedCity) {
      fetchDistrict(selectedCity);
    }
  }, [selectedCity, fetchDistrict]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogHeader>
          <DialogTitle className="text-left">Company Address</DialogTitle>
          <DialogDescription className="text-left">
            <div className="py-4 text-text">
              <p className="mb-4">
                Updated information will only be applied to future documents. Make sure to fill in the correct data.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  name="country" // Nama field
                  control={form.control} // Kontrol dari useForm
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold text-text">Country</FormLabel>
                      <Input className="col-span-3" {...field} value={field.value ?? ''} disabled />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* State / Provinsi */}
                <FormField
                  name="state"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          const selectedProv = provinsi?.find((data) => data.text === value);
                          field.onChange(value); // Simpan data.text dalam form
                          setSelectedProvinsi(selectedProv?.id || ''); // Simpan data.id untuk fetching
                          form.setValue('city', ''); // Reset City
                          form.setValue('district', ''); // Reset District
                        }}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={loading ? 'Loading...' : 'Select state'} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {loading ? (
                            <div className="p-2 text-gray-500 text-center">Loading...</div>
                          ) : (
                            provinsi?.map((data) => (
                              <SelectItem key={data.id} value={data.text}>
                                {data.text}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                {/* City / Kota */}
                <FormField
                  name="city"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          const selectedCty = city?.find((data) => data.text === value);
                          field.onChange(value);
                          setSelectedCity(selectedCty?.id || '');
                          form.setValue('district', ''); // Reset District
                        }}
                        disabled={!selectedProvinsi || loading}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={loading ? 'Loading...' : 'Select city'} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {loading ? (
                            <div className="p-2 text-gray-500 text-center">Loading...</div>
                          ) : city?.length ? (
                            city?.map((data) => (
                              <SelectItem key={data.id} value={data.text}>
                                {data.text}
                              </SelectItem>
                            ))
                          ) : (
                            <div className="p-2 text-gray-500 text-center">No cities available</div>
                          )}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                {/* District / Kec */}
                <FormField
                  name="district"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>District</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          const selectedDist = district?.find((data) => data.text === value);
                          field.onChange(value);
                          setSelectedDistrict(selectedDist?.id || '');
                        }}
                        disabled={!selectedCity || loading}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={loading ? 'Loading...' : 'Select district'} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {loading ? (
                            <div className="p-2 text-gray-500 text-center">Loading...</div>
                          ) : district?.length ? (
                            district?.map((data) => (
                              <SelectItem key={data.id} value={data.text}>
                                {data.text}
                              </SelectItem>
                            ))
                          ) : (
                            <div className="p-2 text-gray-500 text-center">No districts available</div>
                          )}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                <FormField
                  name="address_line_1" // Nama field
                  control={form.control} // Kontrol dari useForm
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold text-text">Address Line 1</FormLabel>
                      <Input className="col-span-3" {...field} value={field.value ?? ''} />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="address_line_2" // Nama field
                  control={form.control} // Kontrol dari useForm
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold text-text">Address Line 2</FormLabel>
                      <Input className="col-span-3" {...field} value={field.value ?? ''} />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  name="zip_code" // Nama field
                  control={form.control} // Kontrol dari useForm
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold text-text">Zip Code</FormLabel>
                      <Input className="col-span-3" {...field} value={field.value ?? ''} />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-row items-center justify-end gap-2">
          <Button type="submit" variant="default" className="w-max" disabled={useAuth().loading}>
            {useAuth().loading ? 'Saving...' : 'Save'}
          </Button>
          <DialogClose ref={closeRef}>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </form>
    </Form>
  );
}
